import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle, ArrowRight, Building2, CalendarClock, CheckCircle2,
  Ban, ClipboardList, CreditCard, DollarSign, FileText, ListTodo, Plus, Search, SlidersHorizontal, Target, Trash2, UserCheck, Users,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const REQUEST_LABELS = {
  book_stay: "Réservation de séjour",
  buy_hospitality: "Réservation de séjour",
  rent_hospitality: "Réservation de séjour",
  rent_professional: "Réservation d’espace",
  rent_real_estate: "Location immobilière",
  buy_real_estate: "Achat immobilier",
  sell_real_estate: "Vente immobilière",
  rent_auto: "Location de véhicule",
  buy_auto: "Achat de véhicule",
  sell_auto: "Vente de véhicule",
  service_request: "Demande de service",
  listing_interest: "Intérêt pour une annonce",
};

const STATUS_LABELS = {
  nouveau: "Nouveau",
  en_cours: "En cours",
  repondu: "Répondu",
  termine: "Terminé",
  annule: "Annulé",
};

const STATUS_TONES = {
  nouveau: "bg-blue-50 text-blue-700",
  en_cours: "bg-amber-50 text-amber-700",
  repondu: "bg-violet-50 text-violet-700",
  termine: "bg-emerald-50 text-emerald-700",
  annule: "bg-red-50 text-red-700",
};

const SECTOR_LABELS = {
  immobilier: "Immobilier",
  real_estate: "Immobilier",
  auto: "Automobile",
  hospitality: "Hôtels & séjours",
  professional: "Espaces & événements",
  business: "Business & Projects",
  services: "Services",
};

const PRIORITY_LABELS = { low: "Basse", normal: "Normale", high: "Haute", urgent: "Urgente" };
const PRIORITY_TONES = {
  low: "bg-slate-100 text-slate-600",
  normal: "bg-blue-50 text-blue-700",
  high: "bg-amber-50 text-amber-700",
  urgent: "bg-red-50 text-red-700",
};

const money = (value) => new Intl.NumberFormat("fr-FR", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
}).format(Number(value || 0));

const dateLabel = (value, withTime = false) => {
  if (!value) return "À planifier";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("fr-FR", withTime
    ? { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }
    : { day: "2-digit", month: "short", year: "numeric" });
};

const cdf = (value) => `${new Intl.NumberFormat("fr-FR").format(Number(value || 0))} CDF`;
const ACCESS_LABELS = { trialing: "Essai 48 h", active: "Actif", expired: "Expiré", suspended: "Suspendu" };
const ACCESS_TONES = { trialing: "bg-blue-50 text-blue-700", active: "bg-emerald-50 text-emerald-700", expired: "bg-slate-100 text-slate-600", suspended: "bg-red-50 text-red-700" };

export default function Dashboard() {
  const [requests, setRequests] = useState([]);
  const [visits, setVisits] = useState([]);
  const [listings, setListings] = useState([]);
  const [deals, setDeals] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [taskSearch, setTaskSearch] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [subscriptionSummary, setSubscriptionSummary] = useState(null);
  const [customerAction, setCustomerAction] = useState("");

  const loadSubscriptionSummary = useCallback(async () => {
    const { data } = await api.get("/subscriptions/admin/summary");
    setSubscriptionSummary(data);
  }, []);

  useEffect(() => {
    const loaders = [
      api.get("/requests", { params: { limit: 500 } }).then((response) => setRequests(response.data || [])),
      api.get("/visits").then((response) => setVisits(response.data || [])),
      api.get("/listings", { params: { status: "", limit: 500 } }).then((response) => setListings(response.data || [])),
      api.get("/deals").then((response) => setDeals(response.data || [])),
      api.get("/invoices").then((response) => setInvoices(response.data || [])),
      loadSubscriptionSummary(),
    ];
    Promise.allSettled(loaders).then((results) => {
      if (results.some((result) => result.status === "rejected")) {
        setLoadError("Certaines données ne sont pas encore disponibles. Le reste du tableau demeure utilisable.");
      }
    }).finally(() => setLoading(false));
  }, [loadSubscriptionSummary]);

  const toggleCustomer = async (customer) => {
    const suspend = customer.access_status !== "suspended";
    if (!window.confirm(`${suspend ? "Suspendre" : "Réactiver"} le compte de ${customer.name || customer.email} ?`)) return;
    setCustomerAction(customer.id);
    try {
      await api.patch(`/subscriptions/admin/customers/${customer.id}/status`, { suspended: suspend });
      await loadSubscriptionSummary();
      toast.success(suspend ? "Compte suspendu" : "Compte réactivé");
    } catch (error) { toast.error(error.response?.data?.detail || "Action impossible"); }
    finally { setCustomerAction(""); }
  };

  const deleteCustomer = async (customer) => {
    if (!window.confirm(`Supprimer définitivement le compte de ${customer.name || customer.email} et ses demandes ? Cette action est irréversible.`)) return;
    setCustomerAction(customer.id);
    try {
      await api.delete(`/subscriptions/admin/customers/${customer.id}`);
      await loadSubscriptionSummary();
      toast.success("Compte supprimé");
    } catch (error) { toast.error(error.response?.data?.detail || "Suppression impossible"); }
    finally { setCustomerAction(""); }
  };

  const extendCustomer = async (customer) => {
    setCustomerAction(customer.id);
    try {
      await api.post(`/customers/${customer.id}/subscription`, { days: 30, payment_reference: "Accès accordé par Owner" });
      await loadSubscriptionSummary();
      toast.success("Accès prolongé de 30 jours");
    } catch (error) { toast.error(error.response?.data?.detail || "Prolongation impossible"); }
    finally { setCustomerAction(""); }
  };

  const now = new Date();
  const todayKey = now.toISOString().slice(0, 10);

  const newRequests = requests.filter((item) => item.status === "nouveau");
  const activeRequests = requests.filter((item) => ["nouveau", "en_cours"].includes(item.status));
  const overdueRequests = requests.filter((item) => {
    const due = item.next_action_at || item.reminder_at;
    return due && new Date(due) <= now && !["termine", "annule"].includes(item.status);
  });
  const upcomingVisits = visits
    .filter((item) => item.date >= todayKey && !["completed", "cancelled"].includes(item.status))
    .sort((a, b) => String(a.date + " " + (a.time || "")).localeCompare(String(b.date + " " + (b.time || ""))));
  const openDeals = deals.filter((item) => !item.closed && !["client", "perdu"].includes(item.stage));
  const pipelineValue = openDeals.reduce((sum, item) => sum + Number(item.price || item.amount || 0), 0);
  const unpaidInvoices = invoices.filter((item) => item.status === "pending");
  const amountDue = unpaidInvoices.reduce((sum, item) => sum + Number(item.total || 0), 0);
  const activeListings = listings.filter((item) => ["available", "negotiation", "pending"].includes(item.status)).length;

  const recentRequests = useMemo(() => [...requests]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 6), [requests]);

  const priorityRows = activeRequests
    .slice()
    .sort((a, b) => {
      const priorityOrder = { urgent: 0, high: 1, normal: 2, low: 3 };
      const priorityDiff = (priorityOrder[a.priority] ?? 2) - (priorityOrder[b.priority] ?? 2);
      if (priorityDiff !== 0) return priorityDiff;
      return new Date(a.next_action_at || a.reminder_at || "2999-12-31") - new Date(b.next_action_at || b.reminder_at || "2999-12-31");
    })
    .slice(0, 20);

  const visiblePriorityRows = priorityRows.filter((item) => {
    if (priorityFilter !== "all" && (item.priority || "normal") !== priorityFilter) return false;
    const value = taskSearch.trim().toLowerCase();
    if (!value) return true;
    return [item.name, item.number, item.owner_assignee, item.next_action, item.service_name, REQUEST_LABELS[item.request_type]]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(value);
  });

  const actions = (() => {
    const requestActions = activeRequests
      .filter((item) => item.next_action || item.next_action_at || item.reminder_at)
      .map((item) => ({
        id: "request-" + item.id,
        kind: "Demande",
        title: item.next_action || "Recontacter " + (item.name || "le client"),
        detail: item.name || item.number,
        date: item.next_action_at || item.reminder_at,
        to: "/admin/requests",
      }));
    const visitActions = upcomingVisits.map((item) => ({
      id: "visit-" + item.id,
      kind: item.visit_type === "essai" ? "Essai" : "Visite",
      title: item.listing?.title || "Rendez-vous client",
      detail: item.lead?.name || item.client_name || "Client",
      date: item.date + "T" + (item.time || "09:00"),
      to: "/admin/bookings",
    }));
    return [...requestActions, ...visitActions]
      .sort((a, b) => new Date(a.date || "2999-12-31") - new Date(b.date || "2999-12-31"))
      .slice(0, 7);
  })();

  const cards = [
    { label: "Nouvelles demandes", value: newRequests.length, note: activeRequests.length + " à traiter", icon: ClipboardList, to: "/admin/requests", tone: "bg-blue-50 text-blue-700" },
    { label: "Actions en retard", value: overdueRequests.length, note: overdueRequests.length ? "À traiter maintenant" : "Aucun retard", icon: AlertCircle, to: "/admin/requests", tone: overdueRequests.length ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700" },
    { label: "Pipeline ouvert", value: money(pipelineValue), note: openDeals.length + " transaction(s)", icon: Target, to: "/admin/deals", tone: "bg-violet-50 text-violet-700" },
    { label: "Montants à recevoir", value: money(amountDue), note: unpaidInvoices.length + " paiement(s) en attente", icon: DollarSign, to: "/admin/invoices", tone: "bg-amber-50 text-amber-700" },
  ];

  return (
    <div className="space-y-5" data-testid="admin-dashboard">
      <div className="flex flex-col gap-4 border-b border-slate-200 pb-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-orange">Owner · Team Work Africa</div>
          <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-brand-navy sm:text-4xl">Tableau de bord</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">Vos priorités, votre équipe, vos transactions et vos paiements sur une seule page.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/admin/deals/new" className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold text-brand-navy hover:border-brand-orange"><Plus className="h-4 w-4" /> Transaction</Link>
          <Link to="/admin/requests" className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-brand-navy px-4 text-sm font-bold text-white hover:bg-brand-navyDeep">Ouvrir l’Inbox <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-slate-200">
        <Link to="/admin" className="whitespace-nowrap border-b-2 border-brand-navy px-3 py-2 text-xs font-bold text-brand-navy">Vue générale</Link>
        <Link to="/admin/requests" className="whitespace-nowrap border-b-2 border-transparent px-3 py-2 text-xs font-semibold text-slate-500 hover:text-brand-navy">Demandes</Link>
        <Link to="/admin/tasks" className="whitespace-nowrap border-b-2 border-transparent px-3 py-2 text-xs font-semibold text-slate-500 hover:text-brand-navy">Toutes les tâches</Link>
        <Link to="/admin/bookings" className="whitespace-nowrap border-b-2 border-transparent px-3 py-2 text-xs font-semibold text-slate-500 hover:text-brand-navy">Calendrier</Link>
        <Link to="/admin/reports" className="whitespace-nowrap border-b-2 border-transparent px-3 py-2 text-xs font-semibold text-slate-500 hover:text-brand-navy">Rapports</Link>
      </div>

      {loadError && <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">{loadError}</div>}
      {loading && <div className="rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-500">Synchronisation des données Owner…</div>}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => (
          <Link key={card.label} to={card.to} className="group rounded-2xl border border-slate-200 bg-white p-4 transition-all hover:-translate-y-0.5 hover:shadow-md">
            <div className={"flex h-10 w-10 items-center justify-center rounded-xl " + card.tone}><card.icon className="h-5 w-5" /></div>
            <div className="mt-4 text-sm font-semibold text-slate-500">{card.label}</div>
            <div className="mt-1 font-display text-2xl font-extrabold text-brand-navy">{card.value}</div>
            <div className="mt-1 flex items-center justify-between text-xs text-slate-400"><span>{card.note}</span><ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></div>
          </Link>
        ))}
      </div>

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="flex flex-col gap-3 border-b border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50 text-violet-700"><Users className="h-5 w-5" /></span><div><h2 className="font-display text-lg font-bold text-brand-navy">Utilisateurs & abonnements</h2><p className="text-xs text-slate-500">Essais, accès payants, échéances et paiements TeamWork Pay.</p></div></div>
          <div className="text-xs font-bold text-slate-500">Conversion essai → paiement : <span className="text-brand-navy">{subscriptionSummary?.conversion_percent || 0}%</span></div>
        </div>
        <div className="grid gap-px bg-slate-100 sm:grid-cols-2 xl:grid-cols-6">
          {[
            [Users, "Utilisateurs", subscriptionSummary?.total_customers || 0],
            [CalendarClock, "Essais actifs", subscriptionSummary?.trialing || 0],
            [UserCheck, "Abonnés actifs", subscriptionSummary?.active || 0],
            [CreditCard, "Mensuels / annuels", `${subscriptionSummary?.active_monthly || 0} / ${subscriptionSummary?.active_annual || 0}`],
            [AlertCircle, "Expirés / suspendus", `${subscriptionSummary?.expired || 0} / ${subscriptionSummary?.suspended || 0}`],
            [DollarSign, "Revenu du mois", cdf(subscriptionSummary?.revenue_month_cdf)],
          ].map(([Icon, label, value]) => <div key={label} className="bg-white px-5 py-4"><Icon className="h-4 w-4 text-brand-orange"/><div className="mt-3 text-[11px] font-bold uppercase tracking-wide text-slate-400">{label}</div><div className="mt-1 text-xl font-extrabold text-brand-navy">{value}</div></div>)}
        </div>
        <div className="grid gap-3 border-y border-slate-100 bg-slate-50/70 px-5 py-3 text-xs font-semibold text-slate-600 sm:grid-cols-5">
          <span>Revenu aujourd’hui : <b className="text-brand-navy">{cdf(subscriptionSummary?.revenue_today_cdf)}</b></span>
          <span>Paiements confirmés : <b className="text-brand-navy">{subscriptionSummary?.payments_paid || 0}</b></span>
          <span>En attente : <b className="text-amber-700">{subscriptionSummary?.payments_pending || 0}</b></span>
          <span>Échoués : <b className="text-red-700">{subscriptionSummary?.payments_failed || 0}</b></span>
          <span>Revenu total : <b className="text-brand-navy">{cdf(subscriptionSummary?.revenue_total_cdf)}</b></span>
        </div>
        <div className="max-h-[520px] overflow-auto">
          <table className="w-full min-w-[960px] text-left">
            <thead className="sticky top-0 bg-white"><tr className="border-b border-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-400"><th className="px-5 py-3">Utilisateur</th><th className="px-4 py-3">Accès</th><th className="px-4 py-3">Formule</th><th className="px-4 py-3">Expiration</th><th className="px-4 py-3">Dernier paiement</th><th className="px-5 py-3 text-right">Contrôle Owner</th></tr></thead>
            <tbody>
              {!subscriptionSummary?.customers?.length && <tr><td colSpan={6} className="px-5 py-8 text-center text-sm text-slate-500">Aucun compte utilisateur.</td></tr>}
              {subscriptionSummary?.customers?.map((customer) => <tr key={customer.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/70">
                <td className="px-5 py-3"><div className="font-bold text-brand-navy">{customer.name || "Sans nom"}</div><div className="text-[11px] text-slate-400">{customer.phone} · {customer.email}</div></td>
                <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${ACCESS_TONES[customer.access_status] || ACCESS_TONES.expired}`}>{ACCESS_LABELS[customer.access_status] || customer.access_status}</span></td>
                <td className="px-4 py-3 text-sm font-semibold text-slate-600">{customer.plan_code === "annual" ? "Annuel" : customer.plan_code === "monthly" ? "Mensuel" : "Essai"}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{dateLabel(customer.subscription_ends_at || customer.trial_ends_at, true)}</td>
                <td className="px-4 py-3"><div className="text-xs font-bold text-slate-700">{customer.last_payment_amount ? cdf(customer.last_payment_amount) : "Aucun"}</div><div className="text-[10px] text-slate-400">{customer.last_payment_reference || "-"}</div></td>
                <td className="px-5 py-3"><div className="flex justify-end gap-2"><button disabled={customerAction === customer.id} onClick={() => extendCustomer(customer)} className="inline-flex h-8 items-center gap-1 rounded-lg border border-blue-200 px-2 text-[11px] font-bold text-blue-700 disabled:opacity-50"><Plus className="h-3.5 w-3.5"/>30 j</button><button disabled={customerAction === customer.id} onClick={() => toggleCustomer(customer)} className={`inline-flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-[11px] font-bold disabled:opacity-50 ${customer.access_status === 'suspended' ? 'border-emerald-200 text-emerald-700' : 'border-amber-200 text-amber-700'}`}>{customer.access_status === "suspended" ? <UserCheck className="h-3.5 w-3.5"/> : <Ban className="h-3.5 w-3.5"/>}{customer.access_status === "suspended" ? "Réactiver" : "Suspendre"}</button><button disabled={customerAction === customer.id} onClick={() => deleteCustomer(customer)} className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-red-200 px-2.5 text-[11px] font-bold text-red-700 disabled:opacity-50"><Trash2 className="h-3.5 w-3.5"/>Supprimer</button></div></td>
              </tr>)}
            </tbody>
          </table>
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="border-b border-slate-100 px-4 py-4 sm:px-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-mist text-brand-navy"><ListTodo className="h-5 w-5" /></span><div><h2 className="font-display text-lg font-bold text-brand-navy">Priorités de l’équipe</h2><p className="text-xs text-slate-500">Chaque dossier doit avoir un responsable et une prochaine action.</p></div></div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <label className="flex h-9 min-w-60 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3"><Search className="h-4 w-4 text-slate-400"/><input value={taskSearch} onChange={(event) => setTaskSearch(event.target.value)} placeholder="Rechercher une tâche…" className="min-w-0 flex-1 bg-transparent text-xs outline-none"/></label>
              <label className="flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3"><SlidersHorizontal className="h-4 w-4 text-slate-400"/><select value={priorityFilter} onChange={(event) => setPriorityFilter(event.target.value)} className="bg-transparent text-xs font-semibold text-slate-600 outline-none"><option value="all">Toutes les priorités</option><option value="urgent">Urgentes</option><option value="high">Hautes</option><option value="normal">Normales</option><option value="low">Basses</option></select></label>
              <Link to="/admin/tasks" className="inline-flex h-9 items-center justify-center rounded-lg bg-brand-navy px-3 text-xs font-bold text-white">Toutes les tâches</Link>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-left">
            <thead><tr className="border-b border-slate-100 bg-slate-50/70 text-[10px] font-bold uppercase tracking-wider text-slate-400"><th className="px-5 py-3">Client</th><th className="px-4 py-3">Besoin</th><th className="px-4 py-3">Responsable</th><th className="px-4 py-3">Prochaine action</th><th className="px-4 py-3">Priorité</th><th className="px-4 py-3">Avancement</th><th className="px-5 py-3 text-right"></th></tr></thead>
            <tbody>
              {visiblePriorityRows.length === 0 && !loading && <tr><td colSpan={7} className="px-5 py-10 text-center text-sm text-slate-500">Aucune priorité en attente.</td></tr>}
              {visiblePriorityRows.map((item) => {
                const progress = item.status === "en_cours" ? 55 : item.status === "repondu" ? 80 : 20;
                return <tr key={item.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
                  <td className="px-5 py-3.5"><div className="font-bold text-brand-navy">{item.name || "Client à identifier"}</div><div className="text-[11px] text-slate-400">{item.number}</div></td>
                  <td className="max-w-[220px] px-4 py-3.5"><div className="truncate text-sm font-semibold text-slate-700">{item.service_name || REQUEST_LABELS[item.request_type] || "Demande client"}</div><div className="text-[11px] text-slate-400">{SECTOR_LABELS[item.sector || item.details?.sector] || "Team Work"}</div></td>
                  <td className="px-4 py-3.5 text-sm text-slate-600">{item.owner_assignee || "À assigner"}</td>
                  <td className="px-4 py-3.5"><div className="max-w-[190px] truncate text-sm text-slate-700">{item.next_action || "Qualifier la demande"}</div><div className="text-[11px] text-slate-400">{dateLabel(item.next_action_at || item.reminder_at, true)}</div></td>
                  <td className="px-4 py-3.5"><span className={"rounded-full px-2.5 py-1 text-[10px] font-bold " + (PRIORITY_TONES[item.priority] || PRIORITY_TONES.normal)}>{PRIORITY_LABELS[item.priority] || "Normale"}</span></td>
                  <td className="px-4 py-3.5"><div className="flex items-center gap-2"><div className="h-1.5 w-20 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-brand-orange" style={{ width: progress + "%" }} /></div><span className="text-[11px] font-semibold text-slate-500">{progress}%</span></div></td>
                  <td className="px-5 py-3.5 text-right"><Link to="/admin/requests" className="text-xs font-bold text-brand-orange hover:underline">Ouvrir</Link></td>
                </tr>;
              })}
            </tbody>
          </table>
        </div>
      </section>

      <div className="grid gap-4 xl:grid-cols-[1.35fr_0.65fr]">
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-4 sm:px-5">
            <div><h2 className="font-display text-lg font-bold text-brand-navy">Dernières demandes</h2><p className="text-xs text-slate-500">Les nouveaux dossiers arrivés depuis le site.</p></div>
            <Link to="/admin/requests" className="text-xs font-bold text-brand-orange hover:underline">Voir l’Inbox</Link>
          </div>
          <div className="divide-y divide-slate-100">
            {recentRequests.length === 0 && !loading && <div className="p-8 text-center text-sm text-slate-500">Aucune demande reçue.</div>}
            {recentRequests.map((item) => (
              <Link key={item.id} to="/admin/requests" className="flex flex-col gap-2 px-4 py-4 hover:bg-slate-50 sm:flex-row sm:items-center sm:px-5">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="font-bold text-brand-navy">{item.name || "Client à identifier"}</div>
                    <span className={"rounded-full px-2 py-0.5 text-[10px] font-bold " + (STATUS_TONES[item.status] || "bg-slate-100 text-slate-600")}>{STATUS_LABELS[item.status] || "À qualifier"}</span>
                  </div>
                  <div className="mt-1 truncate text-sm text-slate-600">{item.service_name || REQUEST_LABELS[item.request_type] || item.listing_title || "Demande client"}</div>
                </div>
                <div className="flex shrink-0 items-center gap-3 text-xs text-slate-400">
                  <span>{SECTOR_LABELS[item.sector || item.details?.sector] || "Team Work"}</span>
                  <span>{dateLabel(item.created_at)}</span>
                  <ArrowRight className="h-4 w-4 text-brand-orange" />
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-4 sm:px-5">
            <div><h2 className="font-display text-lg font-bold text-brand-navy">Aujourd’hui & ensuite</h2><p className="text-xs text-slate-500">Relances, visites et essais.</p></div>
            <Link to="/admin/bookings" className="text-xs font-bold text-brand-orange hover:underline">Calendrier</Link>
          </div>
          <div className="divide-y divide-slate-100">
            {actions.length === 0 && !loading && <div className="p-8 text-center text-sm text-slate-500">Aucune action planifiée.</div>}
            {actions.map((action) => (
              <Link key={action.id} to={action.to} className="flex gap-3 px-4 py-3.5 hover:bg-slate-50 sm:px-5">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-mist text-brand-navy"><CalendarClock className="h-4 w-4" /></div>
                <div className="min-w-0 flex-1"><div className="text-[10px] font-bold uppercase tracking-wider text-brand-orange">{action.kind}</div><div className="truncate text-sm font-bold text-brand-navy">{action.title}</div><div className="truncate text-xs text-slate-500">{action.detail}</div></div>
                <div className="shrink-0 text-right text-[11px] font-semibold text-slate-500">{dateLabel(action.date, true)}</div>
              </Link>
            ))}
          </div>
        </section>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="mb-4 flex items-center justify-between">
          <div><h2 className="font-display text-lg font-bold text-brand-navy">Actions rapides</h2><p className="text-xs text-slate-500">Les opérations les plus utilisées.</p></div>
          <div className="hidden text-xs font-semibold text-slate-400 sm:block">{activeListings} annonce(s) active(s)</div>
        </div>
        <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: "Traiter une demande", to: "/admin/requests", icon: ClipboardList },
            { label: "Créer une transaction", to: "/admin/deals/new", icon: Target },
            { label: "Ajouter une annonce", to: "/admin/listings/new", icon: Building2 },
            { label: "Créer un document", to: "/admin/invoices", icon: FileText },
          ].map((action) => (
            <Link key={action.label} to={action.to} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 text-sm font-bold text-brand-navy transition-colors hover:border-brand-orange hover:bg-brand-orangeSoft/30">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-orange text-white"><action.icon className="h-4 w-4" /></span>
              <span className="flex-1">{action.label}</span>
              <Plus className="h-4 w-4 text-slate-400" />
            </Link>
          ))}
        </div>
      </section>

      <div className="grid gap-3 sm:grid-cols-3">
        <Link to="/admin/deals" className="rounded-2xl bg-brand-navy p-5 text-white"><Target className="h-5 w-5 text-brand-orange" /><div className="mt-3 text-sm font-bold">Transactions</div><div className="mt-1 text-xs text-white/60">Suivre négociation, paiement et marge.</div></Link>
        <Link to="/admin/bookings" className="rounded-2xl bg-white p-5 text-brand-navy ring-1 ring-slate-200"><CalendarClock className="h-5 w-5 text-brand-orange" /><div className="mt-3 text-sm font-bold">Calendrier</div><div className="mt-1 text-xs text-slate-500">{upcomingVisits.length} rendez-vous à venir.</div></Link>
        <Link to="/admin/invoices" className="rounded-2xl bg-white p-5 text-brand-navy ring-1 ring-slate-200"><CheckCircle2 className="h-5 w-5 text-brand-orange" /><div className="mt-3 text-sm font-bold">Finance</div><div className="mt-1 text-xs text-slate-500">{unpaidInvoices.length} paiement(s) à confirmer.</div></Link>
      </div>
    </div>
  );
}
